import ChatHistoryItem from "./ChatHistoryItem";
import {ChatHistory} from "./types";
import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { animated, useSpring } from 'react-spring';
import { HiChevronDown } from 'react-icons/hi';
import { AiFillPushpin } from 'react-icons/ai';

interface ChatHistorySectionProps {
  label: string;
  items: ChatHistory[];
  onChatClick: (chat_id: number) => void;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  showLoadMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  sentinelClassName?: string;
  expandedProp?: boolean; 
}

const ChatHistorySection: React.FC<ChatHistorySectionProps> = ({ label, items, onChatClick, collapsible = true, defaultExpanded = true, showLoadMore = false, isLoadingMore = false, onLoadMore, sentinelClassName = "px-2 pb-12 ", expandedProp }) => {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const observerRef = useRef<HTMLDivElement>(null);

  const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    if (entries[0].isIntersecting && showLoadMore && !isLoadingMore && onLoadMore) {
      onLoadMore();
    }
  }, [showLoadMore, isLoadingMore, onLoadMore]);

  useEffect(() => {
    if (!observerRef.current || !showLoadMore || !onLoadMore) return;

    const observer = new IntersectionObserver(handleIntersect, {
      threshold: 0.1,
      rootMargin: '50px'
    });

    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [handleIntersect, showLoadMore, onLoadMore]);

  useLayoutEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [items?.length]);

  useEffect(() => {
    const onResize = () => {
      if (contentRef.current) setContentHeight(contentRef.current.scrollHeight);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (typeof expandedProp === 'boolean') {
      setExpanded(expandedProp);
    }
  }, [expandedProp]);

  const heightStyles = useSpring({
    height: expanded ? contentHeight : 0,
    config: { tension: 230, friction: 22 }
  });

  if (!items || items.length === 0) return null;

  return (
    <div className="relative">
      {label && (
        <button
          type="button"
          onClick={() => collapsible && setExpanded(e => !e)}
          className="sticky top-0 mt-2 z-20 bg-gray-100 dark:bg-secondary-dark py-1.5 text-gray-500 dark:text-gray-400 text-2xs sm:text-xs
          font-medium uppercase tracking-wider px-2 sm:px-3  -mx-1  border-gray-200 dark:border-gray-700 w-full text-left flex items-center gap-1"
          aria-expanded={expanded}
        >
          {collapsible && (
            <span className={`transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`}>
              <HiChevronDown className="w-3.5 h-auto" />
            </span>
          )}
          {label === 'Pinned' && (
            <AiFillPushpin className="w-3.5 h-3.5 text-amber-500 " />
          )}
          <span>{label}</span>
        </button>
      )}

      <animated.div style={{ ...heightStyles, pointerEvents: expanded ? 'auto' : 'none' }} className="overflow-hidden">
        <div ref={contentRef} className="flex flex-col pb-1.5 mb-1 sm:mb-4 space-y-0.5 sm:space-y-1">
          {items.map((history: ChatHistory) => (
            <ChatHistoryItem
              key={history.chat_id}
              history={history}
              onChatClick={onChatClick}
              animateOnMount={label === 'Pinned'}
            />
          ))}
          
          {showLoadMore && (
            <div ref={observerRef} className={sentinelClassName}>
              {isLoadingMore && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading more chats...
                </div>
              )}
            </div>
          )}
        </div>
      </animated.div>
    </div>
  );
};

export default ChatHistorySection;